import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import SearchIcon from '@mui/icons-material/Search';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../auth/AuthContext';
import { useFeatureFlags } from '../../features/FeatureFlagsContext';
import DetailModalShell from '../../components/detail/DetailModalShell';
import JsonTreeViewer from '../../components/detail/JsonTreeViewer';
import ViewModeToggle from '../../components/table/ViewModeToggle';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import SelectionActionBar from '../../components/workspace/SelectionActionBar';
import {
  WorkspaceEmptyState,
  WorkspaceErrorState,
  WorkspaceLoadingState,
} from '../../components/workspace/WorkspaceStates';
import {
  formatDate,
  labelize,
  ProviderChips,
  statusColor,
  StatusChip,
} from '../../components/workspace/WorkspaceStatus';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../../components/table/ResizableTable';

const debounceDelay = 300;
const defaultRowsPerPage = 25;
const viewModeOptions = [
  { value: 'cards', label: 'Cards', icon: <GridViewIcon fontSize="small" /> },
  { value: 'table', label: 'Table', icon: <TableRowsIcon fontSize="small" /> },
];

export { formatDate, labelize, ProviderChips, statusColor, StatusChip };

export function ImageIdentity({ src, title, subtitle }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Avatar
        variant="rounded"
        src={src || undefined}
        alt=""
        sx={{ width: 44, height: 44, bgcolor: 'surfaceContainerHigh', flexShrink: 0 }}
      >
        {String(title || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap title={title || ''}>{title || 'Untitled'}</Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" noWrap title={subtitle} sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function renderDetailValue(value) {
  if (React.isValidElement(value)) {
    return value;
  }
  if (value === null || typeof value === 'undefined' || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

function DetailSummary({ item, resource }) {
  const fields = resource.detailFields?.(item) || [];
  const chips = resource.detailChips?.(item) || [];
  const image = resource.image?.(item);
  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        {image ? (
          <Box
            component="img"
            src={image}
            alt=""
            sx={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 2, bgcolor: 'background.default' }}
          />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{resource.itemTitle(item)}</Typography>
          {resource.itemSubtitle?.(item) ? (
            <Typography color="text.secondary">{resource.itemSubtitle(item)}</Typography>
          ) : null}
          {chips.length ? (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              {chips.map((chip) => (
                <Chip
                  key={`${chip.label}-${chip.value || ''}`}
                  size="small"
                  color={chip.color || 'default'}
                  variant={chip.variant || 'outlined'}
                  label={chip.value ? `${chip.label}: ${chip.value}` : chip.label}
                />
              ))}
            </Stack>
          ) : null}
        </Box>
      </Stack>

      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 0.35fr) minmax(0, 1fr)' },
          columnGap: 2,
          rowGap: 0,
          m: 0,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {fields.map((field) => (
          <React.Fragment key={field.label}>
            <Typography component="dt" variant="body2" color="text.secondary" sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
              {field.label}
            </Typography>
            <Box component="dd" sx={{ m: 0, py: 1.25, borderBottom: 1, borderColor: 'divider', minWidth: 0 }}>
              {React.isValidElement(field.value) ? field.value : (
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {renderDetailValue(field.value)}
                </Typography>
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Stack>
  );
}

function storedViewMode(resource) {
  const modes = resource.viewModes || ['table'];
  if (typeof window === 'undefined' || modes.length < 2) {
    return resource.defaultView || modes[0];
  }
  const stored = window.localStorage.getItem(`podboss.view.${resource.key}`);
  return modes.includes(stored) ? stored : (resource.defaultView || modes[0]);
}

function ResourceCard({ item, resource, onOpen, selectable, selected, onToggle }) {
  const image = resource.image?.(item);
  const title = resource.itemTitle(item);
  const subtitle = resource.itemSubtitle?.(item);
  const chips = resource.detailChips?.(item) || [];
  const fields = resource.cardFields?.(item) || [];
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minWidth: 0,
        position: 'relative',
        borderColor: selected ? 'primary.main' : undefined,
        boxShadow: selected ? '0 0 0 1px var(--md-sys-color-primary)' : undefined,
      }}
    >
      {selectable ? (
        <Checkbox
          checked={selected}
          onChange={() => onToggle(item)}
          onClick={(event) => event.stopPropagation()}
          inputProps={{ 'aria-label': `${selected ? 'Deselect' : 'Select'} ${title || 'item'}` }}
          sx={{
            position: 'absolute',
            zIndex: 2,
            top: 6,
            left: 6,
            bgcolor: 'rgba(255,255,255,0.9)',
            borderRadius: '50%',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        />
      ) : null}
      <CardActionArea
        onClick={() => onOpen(item)}
        aria-label={`View ${title}`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left' }}
      >
        <Box sx={{ aspectRatio: '4 / 3', bgcolor: 'var(--md-sys-color-surface-container-low)', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
          {image ? (
            <Box component="img" src={image} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <Typography aria-hidden="true" variant="h3" color="text.secondary">{String(title || '?').charAt(0).toUpperCase()}</Typography>
          )}
        </Box>
        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              title={title || ''}
              sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {title || 'Untitled'}
            </Typography>
            {subtitle ? <Typography variant="body2" color="text.secondary" noWrap title={subtitle}>{subtitle}</Typography> : null}
          </Box>
          {chips.length ? (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {chips.slice(0, 4).map((chip) => (
                <Chip
                  key={`${chip.label}-${chip.value || ''}`}
                  size="small"
                  color={chip.color || 'default'}
                  variant={chip.variant || 'outlined'}
                  label={chip.value ? `${chip.label}: ${chip.value}` : chip.label}
                />
              ))}
            </Stack>
          ) : null}
          {fields.length ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: fields.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1.25, mt: 'auto', pt: 0.5 }}>
              {fields.slice(0, 4).map((field) => (
                <Box key={field.label} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{field.label}</Typography>
                  {React.isValidElement(field.value) ? field.value : (
                    <Typography variant="body2" noWrap title={String(renderDetailValue(field.value))}>{renderDetailValue(field.value)}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const UnifiedReadPage = ({ resource }) => {
  const { fetchWithAuth } = useAuth();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(() => Math.max(0, Number.parseInt(searchParams.get('page') || '1', 10) - 1 || 0));
  const [rowsPerPage, setRowsPerPage] = React.useState(() => {
    const requested = Number.parseInt(searchParams.get('limit') || '', 10);
    return [10, 25, 50, 100].includes(requested) ? requested : defaultRowsPerPage;
  });
  const [search, setSearch] = React.useState(() => searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = React.useState(() => (searchParams.get('q') || '').trim());
  const [filters, setFilters] = React.useState(() => {
    const keys = new Set((resource.filters || []).map((filter) => filter.key));
    (resource.urlFilterKeys || []).forEach((key) => keys.add(key));
    return Object.fromEntries(
      Array.from(keys)
        .map((key) => [key, searchParams.get(key) || ''])
        .filter(([, value]) => value !== ''),
    );
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [detailID, setDetailID] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [viewMode, setViewMode] = React.useState(() => storedViewMode(resource));
  const [selectedItems, setSelectedItems] = React.useState(() => new Map());
  const skipPageReset = React.useRef(false);
  const skipURLSync = React.useRef(false);
  const lastWrittenURL = React.useRef(searchParams.toString());
  const columns = useResizableColumns(resource.columns, { storageKey: `table.unified.${resource.key}` });
  const selectable = resource.selectable === true;

  React.useEffect(() => {
    setViewMode(storedViewMode(resource));
  }, [resource]);

  const changeViewMode = (nextMode) => {
    if (!(resource.viewModes || ['table']).includes(nextMode)) return;
    setViewMode(nextMode);
    window.localStorage.setItem(`podboss.view.${resource.key}`, nextMode);
  };

  React.useEffect(() => {
    const signature = searchParams.toString();
    if (lastWrittenURL.current === signature) {
      lastWrittenURL.current = null;
      return;
    }
    const nextSearch = searchParams.get('q') || '';
    const keys = new Set((resource.filters || []).map((filter) => filter.key));
    (resource.urlFilterKeys || []).forEach((key) => keys.add(key));
    const nextFilters = Object.fromEntries(
      Array.from(keys)
        .map((key) => [key, searchParams.get(key) || ''])
        .filter(([, value]) => value !== ''),
    );
    const requestedPage = Math.max(0, Number.parseInt(searchParams.get('page') || '1', 10) - 1 || 0);
    const requestedLimit = Number.parseInt(searchParams.get('limit') || '', 10);
    const nextLimit = [10, 25, 50, 100].includes(requestedLimit) ? requestedLimit : defaultRowsPerPage;
    skipPageReset.current = true;
    skipURLSync.current = true;
    setSearch((current) => (current === nextSearch ? current : nextSearch));
    setDebouncedSearch((current) => (current === nextSearch.trim() ? current : nextSearch.trim()));
    setFilters((current) => {
      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(nextFilters);
      const unchanged = currentKeys.length === nextKeys.length
        && nextKeys.every((key) => current[key] === nextFilters[key]);
      return unchanged ? current : nextFilters;
    });
    setPage((current) => (current === requestedPage ? current : requestedPage));
    setRowsPerPage((current) => (current === nextLimit ? current : nextLimit));
  }, [resource, searchParams]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false;
      return;
    }
    setPage(0);
  }, [debouncedSearch, filters, rowsPerPage]);

  React.useEffect(() => {
    if (skipURLSync.current) {
      skipURLSync.current = false;
      return;
    }
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (debouncedSearch) next.set('q', debouncedSearch); else next.delete('q');
      const filterKeys = new Set((resource.filters || []).map((filter) => filter.key));
      (resource.urlFilterKeys || []).forEach((key) => filterKeys.add(key));
      filterKeys.forEach((key) => next.delete(key));
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && typeof value !== 'undefined') next.set(key, String(value));
      });
      if (page > 0) next.set('page', String(page + 1)); else next.delete('page');
      if (rowsPerPage !== defaultRowsPerPage) next.set('limit', String(rowsPerPage)); else next.delete('limit');
      if (next.toString() === current.toString()) return current;
      lastWrittenURL.current = next.toString();
      return next;
    }, { replace: true });
  }, [debouncedSearch, filters, page, resource, rowsPerPage, setSearchParams]);

  const filterSignature = JSON.stringify(filters);
  React.useEffect(() => {
    setSelectedItems(new Map());
  }, [debouncedSearch, filterSignature, resource.key]);

  const loadItems = React.useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
      });
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && typeof value !== 'undefined') {
          params.set(key, String(value));
        }
      });
      const response = await fetchWithAuth(`${config.apiBaseUrl}${resource.endpoint}?${params.toString()}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `Failed to load ${resource.title.toLowerCase()}`);
      }
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (loadError) {
      setError(loadError.message || `Failed to load ${resource.title.toLowerCase()}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [debouncedSearch, fetchWithAuth, filters, page, resource, rowsPerPage]);

  React.useEffect(() => {
    loadItems();
  }, [loadItems]);

  React.useEffect(() => {
    if (!resource.pollInterval) return undefined;
    const handle = window.setInterval(() => loadItems({ silent: true }), resource.pollInterval);
    return () => window.clearInterval(handle);
  }, [loadItems, resource.pollInterval]);

  const openDetail = React.useCallback(async (item) => {
    const id = item?.id;
    if (!id) {
      return;
    }
    if (flags.unified_writes && resource.detailPath) {
      navigate(typeof resource.detailPath === 'function' ? resource.detailPath(item) : resource.detailPath);
      return;
    }
    setDetailID(id);
    setDetail(item);
    const detailEndpoint = typeof resource.detailEndpoint === 'function'
      ? resource.detailEndpoint(item)
      : `${resource.endpoint}/${id}`;
    if (!detailEndpoint) {
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}${detailEndpoint}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load details');
      }
      setDetail(payload.item || item);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load details');
      setDetailID(null);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [fetchWithAuth, flags.unified_writes, navigate, resource]);

  const closeDetail = () => {
    setDetailID(null);
    setDetail(null);
    setDetailLoading(false);
  };

  const toggleSelection = React.useCallback((item) => {
    if (!selectable || !item?.id) return;
    setSelectedItems((current) => {
      const next = new Map(current);
      if (next.has(item.id)) next.delete(item.id); else next.set(item.id, item);
      return next;
    });
  }, [selectable]);

  const pageSelectedCount = items.reduce((count, item) => count + (selectedItems.has(item.id) ? 1 : 0), 0);
  const allOnPageSelected = items.length > 0 && pageSelectedCount === items.length;
  const someOnPageSelected = pageSelectedCount > 0;
  const togglePageSelection = () => {
    setSelectedItems((current) => {
      const next = new Map(current);
      if (allOnPageSelected) items.forEach((item) => next.delete(item.id));
      else items.forEach((item) => next.set(item.id, item));
      return next;
    });
  };

  const selectedValues = Array.from(selectedItems.values());

  const refreshDetail = React.useCallback(async () => {
    if (!detail) return;
    await Promise.all([openDetail(detail), loadItems()]);
  }, [detail, loadItems, openDetail]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }} aria-busy={loading ? 'true' : undefined}>
      <WorkspacePageHeader
        title={resource.title}
        description={resource.description}
        breadcrumbs={[{ label: resource.title }]}
        actions={(
          <>
          {(resource.viewModes || []).length > 1 ? (
            <ViewModeToggle value={viewMode} onChange={changeViewMode} options={viewModeOptions} />
          ) : null}
          {flags.unified_writes && resource.createPath ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(resource.createPath)}
            >
              {resource.createLabel || `New ${resource.itemSingular || 'item'}`}
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />}
            onClick={loadItems}
            disabled={loading}
          >
            Refresh
          </Button>
          </>
        )}
      />

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label={`Search ${resource.title.toLowerCase()}`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          {(resource.filters || []).map((filter) => (
            <TextField
              key={filter.key}
              select
              size="small"
              label={filter.label}
              value={filters[filter.key] || ''}
              onChange={(event) => setFilters((current) => ({ ...current, [filter.key]: event.target.value }))}
              sx={{ minWidth: filter.minWidth || 170 }}
            >
              {filter.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          ))}
        </Stack>
      </Paper>

      {error ? <WorkspaceErrorState message={error} onRetry={loadItems} /> : null}

      <SelectionActionBar
        selectedCount={selectedItems.size}
        pageCount={pageSelectedCount}
        allOnPageSelected={allOnPageSelected}
        someOnPageSelected={someOnPageSelected}
        onTogglePage={togglePageSelection}
        onClear={() => setSelectedItems(new Map())}
      >
        {selectedValues.length === 1 ? <Button color="inherit" onClick={() => openDetail(selectedValues[0])}>Open selected</Button> : null}
        {resource.renderSelectionActions?.({
          selectedItems: selectedValues,
          clearSelection: () => setSelectedItems(new Map()),
          navigate,
          refresh: loadItems,
        })}
      </SelectionActionBar>

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'cards' ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1.5, md: 2 } }}>
            {loading ? (
              <WorkspaceLoadingState label={`Loading ${resource.title.toLowerCase()}…`} />
            ) : items.length === 0 ? (
              <WorkspaceEmptyState title={`No ${resource.title.toLowerCase()} found`} description="Try changing your search or filters." />
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${resource.cardMinWidth || 270}px), 1fr))`, gap: 2 }}>
                {items.map((item) => (
                  <ResourceCard
                    key={item.id}
                    item={item}
                    resource={resource}
                    onOpen={openDetail}
                    selectable={selectable}
                    selected={selectedItems.has(item.id)}
                    onToggle={toggleSelection}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: columns.tableMinWidth + (selectable ? 52 : 0) }}>
              <ResizableColGroup columns={resource.columns} getWidth={columns.getWidth} leadingWidth={selectable ? 52 : 0} />
              <TableHead>
                <TableRow>
                  {selectable ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onChange={togglePageSelection}
                        inputProps={{ 'aria-label': allOnPageSelected ? 'Deselect this page' : 'Select this page' }}
                      />
                    </TableCell>
                  ) : null}
                  {resource.columns.map((column) => (
                    <ResizableHeaderCell
                      key={column.key}
                      column={column}
                      getWidth={columns.getWidth}
                      onResizeStart={columns.startColumnResize}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={resource.columns.length + (selectable ? 1 : 0)} label={`Loading ${resource.title.toLowerCase()}...`} />
                ) : items.length === 0 ? (
                  <TableEmptyRow colSpan={resource.columns.length + (selectable ? 1 : 0)} label={`No ${resource.title.toLowerCase()} found.`} />
                ) : items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    selected={selectedItems.has(item.id)}
                    tabIndex={0}
                    aria-label={`View ${resource.itemTitle(item)}`}
                    onClick={() => openDetail(item)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openDetail(item);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    {selectable ? (
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={selectedItems.has(item.id)}
                          onChange={() => toggleSelection(item)}
                          inputProps={{ 'aria-label': `${selectedItems.has(item.id) ? 'Deselect' : 'Select'} ${resource.itemTitle(item)}` }}
                        />
                      </TableCell>
                    ) : null}
                    {resource.columns.map((column) => (
                      <TableCell key={column.key} sx={{ overflow: 'hidden' }}>
                        {column.render(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <DetailModalShell
        open={Boolean(detailID)}
        onClose={closeDetail}
        loading={detailLoading}
        loadingLabel="Loading canonical details..."
        title={detail ? resource.itemTitle(detail) : resource.title}
        externalId={detail?.id}
        left={detail ? <DetailSummary item={detail} resource={resource} /> : null}
        right={detail ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Canonical data</Typography>
            <Paper variant="outlined" sx={{ p: 1, maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
              <JsonTreeViewer data={detail} />
            </Paper>
          </Box>
        ) : null}
        actions={detail && resource.detailActions
          ? resource.detailActions(detail, { refresh: refreshDetail, close: closeDetail })
          : null}
      />
    </Box>
  );
};

export default UnifiedReadPage;
